/**
 * useGeolocation — shared composable for requesting precise GPS location.
 *
 * Checks the Permissions API first so we know the current state before
 * calling getCurrentPosition. Returns a structured result so callers
 * can show the right UI for each state.
 *
 * States:
 *   granted   — permission already allowed, position returned immediately
 *   prompt    — browser will show the native permission dialog
 *   denied    — user previously denied; we can't request again, show instructions
 *   unavailable — browser doesn't support geolocation
 */

export const useGeolocation = () => {

  /**
   * Request the user's precise GPS position.
   * @returns {{ coords: {lat, lng} } | { error: string, denied: boolean }}
   */
  const requestLocation = () => new Promise(async (resolve) => {

    if (!navigator.geolocation) {
      return resolve({ error: 'Your browser does not support location services.', denied: false });
    }

    // Check current permission state before triggering the native prompt
    let permState = 'prompt'; // default — assume we can ask
    if (navigator.permissions?.query) {
      try {
        const status = await navigator.permissions.query({ name: 'geolocation' });
        permState = status.state; // 'granted' | 'denied' | 'prompt'
      } catch {
        // permissions API not supported — fall through and try anyway
      }
    }

    if (permState === 'denied') {
      return resolve({
        error: 'Location access is blocked. To enable it: open your browser settings → Site permissions → Location → Allow for this site.',
        denied: true,
      });
    }

    // Either 'granted' or 'prompt' — call getCurrentPosition
    // The browser will show its native dialog if state is 'prompt'
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({
        coords: { lat: coords.latitude, lng: coords.longitude },
      }),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          resolve({
            error: 'Location access was denied. Please allow location in your browser settings and try again.',
            denied: true,
          });
        } else if (err.code === err.TIMEOUT) {
          resolve({ error: 'Location request timed out. Please try again.', denied: false });
        } else {
          resolve({ error: 'Unable to get your location. Please try again.', denied: false });
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });

  return { requestLocation };
};
